import React, { useRef, useState } from "react";
import useRevealSequence from "../useRevealSequence";
import Celebration from "../Celebration";

/**
 * Coin Crash - a coin picks the winner by smashing it open.
 *
 * While the round finishes the coin circles the board and feints at a
 * yantra now and then, so the wait is a chase rather than a pause. When
 * the result arrives the board dims, a reticle snaps onto the winner,
 * and the coin comes down on it: the frost cracks, shatters into flying
 * debris, and the yantra underneath comes up lit.
 *
 * Built from docs/animation-samples/Yantra_Coin_Crash_V3.html.
 *
 * The coin and the debris are moved by writing transforms straight onto
 * their nodes rather than through React state: this runs every frame on
 * an outlet's tablet while the rest of the live page is mounted, and a
 * re-render per frame is a cost it cannot carry.
 */

const STAGE = 820;
const MID = { x: STAGE / 2, y: 400 };

/* After the winner is known: lock on, lift, drop. Short - the round is
   already over and the next one has started. */
const LOCK_MS = 240;
const LIFT_MS = 220;
const DIVE_MS = 200;
const HOLD_MS = 1200;

const easeOutQuart = (p) => 1 - Math.pow(1 - p, 4);
const easeInCubic = (p) => p * p * p;
const rand = (a, b) => a + Math.random() * (b - a);

export default function CoinCrash({ tiles, winnerIndex, onFinished }) {
  const count = tiles.length;

  const stageRef = useRef(null);
  const coinRef = useRef(null);
  const fxRef = useRef(null);
  const cardRefs = useRef([]);
  const flyingRef = useRef(true);

  const [target, setTarget] = useState(-1);
  const [flinch, setFlinch] = useState(-1);
  const [phase, setPhase] = useState("hunting"); // hunting | locked | hit | won

  /** Where a tile sits inside the fixed 820 stage. */
  const centreOf = (i) => {
    const el = cardRefs.current[i];
    const stage = stageRef.current;
    if (!el || !stage) return MID;
    const s = stage.getBoundingClientRect();
    const r = el.getBoundingClientRect();
    // The stage is scaled as one piece, so undo that scale to get back to
    // its own 820-wide coordinates.
    const scale = s.width / STAGE || 1;
    return {
      x: (r.left - s.left + r.width / 2) / scale,
      y: (r.top - s.top + r.height / 2) / scale,
    };
  };

  const coinAt = useRef({ x: MID.x, y: -120 });

  const placeCoin = (x, y, scale = 1, spin = 0) => {
    coinAt.current = { x, y };
    const el = coinRef.current;
    if (el) {
      el.style.transform = `translate(${x}px,${y}px) rotateY(${spin}deg) scale(${scale})`;
    }
  };

  const dropTrail = (x, y) => {
    const host = fxRef.current;
    if (!host) return;
    const t = document.createElement("i");
    t.className = "rv-cc-trail";
    t.style.transform = `translate(${x}px,${y}px)`;
    host.appendChild(t);
    setTimeout(() => t.remove(), 520);
  };

  /**
   * Move the coin along `path` over `ms`. Raced against a plain timer,
   * because a browser stops calling requestAnimationFrame when its window
   * is not in front - and a flight that never lands would leave the whole
   * reveal hanging on the board.
   */
  const fly = (ctx, path, ms, opts = {}) => {
    const {
      spin = 720,
      from = 1,
      to = 1,
      easing = easeOutQuart,
      trail = 0,
      stopWhen = null,
    } = opts;

    const start = performance.now();
    const animation = new Promise((resolve) => {
      let lastTrail = 0;
      const frame = (now) => {
        if (stopWhen && stopWhen()) {
          resolve("cut");
          return;
        }
        const raw = Math.min(1, (now - start) / ms);
        const p = easing(raw);
        const { x, y } = path(p);
        placeCoin(x, y, from + (to - from) * p, spin * p);
        if (trail && now - lastTrail > trail) {
          lastTrail = now;
          dropTrail(x, y);
        }
        if (raw < 1) requestAnimationFrame(frame);
        else resolve();
      };
      requestAnimationFrame(frame);
    });

    // The timer that rescues a flight when no frames are coming has to
    // watch for the result too, or a browser in the background would sit
    // out the whole lap before noticing the round had ended.
    const guard = (async () => {
      const deadline = performance.now() + ms + 120;
      while (performance.now() < deadline) {
        if (stopWhen && stopWhen()) return "cut";
        await ctx.sleep(80);
      }
      return "timeout";
    })();

    return Promise.race([animation, guard]).then((how) => {
      // A cut flight stays where it was; a finished one is snapped to its
      // end point, in case the frames stopped coming part way.
      if (how === "cut") return;
      const end = path(1);
      placeCoin(end.x, end.y, to, spin);
    });
  };

  /* -------------------------------------------------------- the impact */

  const shockwave = (x, y) => {
    const host = fxRef.current;
    if (!host) return;
    const s = document.createElement("i");
    s.className = "rv-cc-shock";
    s.style.left = `${x}px`;
    s.style.top = `${y}px`;
    host.appendChild(s);
    setTimeout(() => s.remove(), 720);
  };

  /** Frost shards and sparks thrown out of the tile, with gravity. */
  const debris = (x, y) => {
    const host = fxRef.current;
    if (!host) return;

    const bits = [];
    for (let i = 0; i < 24; i++) {
      const el = document.createElement("i");
      el.className = i % 3 === 0 ? "rv-cc-spark" : "rv-cc-shard";
      el.style.left = `${x}px`;
      el.style.top = `${y}px`;
      host.appendChild(el);
      const a = rand(0, Math.PI * 2);
      const speed = rand(220, 640);
      bits.push({
        el,
        x: 0,
        y: 0,
        vx: Math.cos(a) * speed,
        vy: Math.sin(a) * speed - rand(100, 300),
        rot: rand(0, 360),
        vr: rand(-640, 640),
        life: rand(0.6, 1.05),
        age: 0,
      });
    }

    let last = performance.now();
    const step = (now) => {
      const dt = Math.min(48, now - last) / 1000;
      last = now;
      let alive = false;
      for (const b of bits) {
        if (b.age > b.life) {
          if (b.el.isConnected) b.el.remove();
          continue;
        }
        alive = true;
        b.age += dt;
        b.vy += 1500 * dt;
        b.x += b.vx * dt;
        b.y += b.vy * dt;
        b.rot += b.vr * dt;
        const fade = 1 - b.age / b.life;
        b.el.style.transform = `translate(${b.x}px,${b.y}px) rotate(${b.rot}deg) scale(${fade})`;
        b.el.style.opacity = String(fade);
      }
      if (alive) requestAnimationFrame(step);
      else bits.forEach((b) => b.el.isConnected && b.el.remove());
    };
    requestAnimationFrame(step);

    // If frames never come, clear up on a timer instead of leaving debris
    // frozen mid-air.
    setTimeout(() => bits.forEach((b) => b.el.isConnected && b.el.remove()), 1500);
  };

  /* ------------------------------------------------------- the sequence */

  useRevealSequence(
    async (ctx) => {
      // In from above, spinning.
      placeCoin(MID.x, -120, 0.5, 0);
      await ctx.sleep(30);
      await fly(
        ctx,
        (p) => ({
          x: MID.x + Math.sin(p * Math.PI) * 70,
          y: -120 + (MID.y - 40 + 120) * p,
        }),
        520,
        { spin: 900, from: 0.5, to: 1, trail: 40 },
      );

      // Round the board, feinting at yantras, until the round is over.
      const rx = 300;
      const ry = 250;
      const resultIsIn = () => ctx.winnerIndex() >= 0;
      let lap = 0;
      while (ctx.winnerIndex() < 0) {
        await fly(
          ctx,
          (p) => {
            const a = -Math.PI / 2 + p * Math.PI * 2;
            return { x: MID.x + Math.cos(a) * rx, y: MID.y + Math.sin(a) * ry };
          },
          1400,
          { spin: 1440, easing: (q) => q, trail: 38, stopWhen: resultIsIn },
        );

        if (ctx.winnerIndex() >= 0) break;

        // Every other lap, a feint: it dives at one yantra, which flinches.
        if (lap % 2 === 1 && count > 1) {
          const decoy = Math.floor(Math.random() * count);
          const c = centreOf(decoy);
          await fly(
            ctx,
            (p) => ({
              x: MID.x + (c.x - MID.x) * p,
              y: MID.y + (c.y - MID.y) * p - Math.sin(p * Math.PI) * 60,
            }),
            300,
            { spin: 520, to: 0.85, trail: 26, stopWhen: resultIsIn },
          );
          setFlinch(decoy);
          await ctx.sleep(130);
          setFlinch(-1);
          await fly(
            ctx,
            (p) => ({
              x: c.x + (MID.x - c.x) * p,
              y: c.y + (MID.y - c.y) * p - Math.sin(p * Math.PI) * 60,
            }),
            280,
            { spin: 420, from: 0.85, to: 1, trail: 26, stopWhen: resultIsIn },
          );
        }
        lap += 1;
      }

      const found = ctx.winnerIndex();
      const hit = centreOf(found);

      // Lock on: the rest of the board goes quiet.
      setTarget(found);
      setPhase("locked");
      await ctx.sleep(LOCK_MS);

      // Lift, then down on it.
      const lift = { ...coinAt.current };
      await fly(
        ctx,
        (p) => ({
          x: lift.x + (hit.x - lift.x) * p,
          y: lift.y + (-80 - lift.y) * p,
        }),
        LIFT_MS,
        { spin: 540, to: 1.2, trail: 26 },
      );
      await fly(ctx, (p) => ({ x: hit.x, y: -80 + (hit.y + 80) * p }), DIVE_MS, {
        spin: 900,
        easing: easeInCubic,
        from: 1.2,
        to: 0.9,
        trail: 14,
      });

      // Impact.
      flyingRef.current = false;
      setPhase("hit");
      shockwave(hit.x, hit.y);
      shockwave(hit.x, hit.y);
      debris(hit.x, hit.y);

      await ctx.sleep(260);
      setPhase("won");
      await ctx.sleep(HOLD_MS);
      onFinished();
    },
    winnerIndex,
    [count],
  );

  const winner = phase === "won" && target >= 0 ? tiles[target] : null;
  const hitPoint = target >= 0 ? centreOf(target) : MID;

  return (
    <div
      className={`rv-cc ${phase === "hit" ? "rv-cc--shake" : ""}`}
      ref={stageRef}
    >
      <div className={`rv-cc-grid ${phase !== "hunting" ? "rv-cc-grid--locked" : ""}`} data-count={count}>
        {tiles.map((tile, i) => (
          <div
            key={tile.id}
            ref={(el) => {
              cardRefs.current[i] = el;
            }}
            className={[
              "rv-card rv-cc-card",
              i === flinch ? "rv-cc-card--flinch" : "",
              phase !== "hunting" && i !== target ? "rv-card--dim" : "",
              phase === "locked" && i === target ? "rv-cc-card--target" : "",
              (phase === "hit" || phase === "won") && i === target
                ? "rv-cc-card--hit"
                : "",
              phase === "won" && i === target ? "rv-card--winner rv-cc-card--won" : "",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            <img src={tile.img} alt={tile.name || ""} />
            <span>{tile.name}</span>
            <i className="rv-cc-frost" />
            <svg className="rv-cc-cracks" viewBox="0 0 100 100" preserveAspectRatio="none">
              <path d="M50 50 L18 12" />
              <path d="M50 50 L86 20" />
              <path d="M50 50 L92 62" />
              <path d="M50 50 L64 96" />
              <path d="M50 50 L22 88" />
              <path d="M50 50 L6 54" />
              <path d="M30 30 L44 44" />
              <path d="M70 34 L56 46" />
            </svg>
          </div>
        ))}
      </div>

      {/* The column of light the coin falls through. */}
      {phase === "locked" || phase === "hit" ? (
        <div
          className="rv-cc-beam"
          style={{ left: `${hitPoint.x}px`, height: `${hitPoint.y}px` }}
        />
      ) : null}

      {phase === "locked" ? (
        <div
          className="rv-cc-reticle"
          style={{ left: `${hitPoint.x}px`, top: `${hitPoint.y}px` }}
        >
          <i />
          <i />
          <b />
          <b />
        </div>
      ) : null}

      <div
        className={`rv-cc-coin ${phase === "hit" || phase === "won" ? "rv-cc-coin--spent" : ""}`}
        ref={coinRef}
      >
        ₹
      </div>

      {/* Sparks, shards and shockwaves live here, outside React. */}
      <div className="rv-cc-fx" ref={fxRef} />

      {phase === "hit" || phase === "won" ? <div className="rv-cc-flash" /> : null}

      {winner ? (
        <>
          <div
            className="rv-cc-crown"
            style={{ left: `${hitPoint.x}px`, top: `${hitPoint.y - 110}px` }}
          >
            👑
          </div>
          <div className="rv-cc-banner">{winner.name}</div>
          <Celebration tile={winner} seed={target} compact />
        </>
      ) : null}
    </div>
  );
}
